/**
 * One customer's network: the peers an on-site agent found, the devices monitored over SNMP, and the
 * log of every agent deployment attempted.
 *
 * The port of `Views/NetworkPage.xaml` + `NetworkViewModel` + `DiscoveredRowViewModel`.
 *
 *   * A ROW KEEPS ITS OPEN PANEL ACROSS A REFRESH. The XAML rebuilt every row on every refresh, which
 *     snapped shut the credential form someone was typing into. Rows here are keyed by id and versioned
 *     by what they show - not by `lastSeenUtc`, which every rescan moves - so a rescan leaves an open
 *     panel alone and only a row whose content really changed is rebuilt.
 *   * `MonitorChanged` FIRES ON EVERY POLL and refreshes only the monitors list; `DiscoveryChanged`
 *     (a scan finished, a deployment reported in) refreshes all three.
 *   * A SUCCESSFUL PUSH-INSTALL LEAVES ITS PANEL OPEN, its status saying what happens next, and
 *     refreshes only the deployment log. The XAML once collapsed the panel its own confirmation was
 *     in, so a working install looked like a dead button.
 *   * A Synology is offered its own enrollment (private MIBs plus an optional DSM/SRM login), never
 *     generic monitoring as well, and its manual enrollment is the .spk's server URL and token rather
 *     than a PowerShell line it can never run.
 *   * Admin passwords for a push-install are used once and never kept; the box is emptied whatever the
 *     outcome.
 */
import { insets, type Container, type Control } from 'webwin';
import * as crm from '../api/crm.js';
import * as hub from '../api/hub.js';
import { describeError } from '../api/http.js';
import {
  DiscoveredDeviceKind,
  type DeployLogDto,
  type DiscoveredDeviceDto,
  type SnmpCredentials,
  type SnmpMonitorDto
} from '../contracts.gen.js';
import {
  canAdopt,
  canDeploy,
  canEnrollSynology,
  kindLabel,
  looksSynology,
  normalizeMac,
  snmpSummary,
  stateLabel
} from '../discovery.js';
import { ago, join, localDateTime, plural } from '../format.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { emptyState } from '../ui/EmptyState.js';
import { SnmpForm } from '../ui/SnmpForm.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  passwordBox,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import { synologyEnrollPanel } from './sections/synology-enroll.js';
import type { CrmPage, PageContext } from './page.js';

/** What a discovered row shows. A rescan moves `lastSeenUtc` on every row; that alone rebuilds none. */
function rowVersion(peer: DiscoveredDeviceDto): unknown {
  const { lastSeenUtc: _seen, firstSeenUtc: _first, ...shown } = peer;
  return shown;
}

export function networkPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const customerId = Number(ctx.params.id);
      const { view, content } = scrollPage();
      host.controls.add(view);

      /** The online agent to scan from, and the poller for anything not tied to a discovered row. */
      let agentId: number | null = null;
      let scanning = false;

      // ---- header ----
      const title = h1('Network');
      const subtitle = muted('');
      const pageStatus = new StatusLine();
      const scanButton = button('Scan network', () => void scan(), true);
      scanButton.enabled = false;
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          stackOf(buttonRow(), button('← Customer', () => ctx.go(`/customers/${customerId}`))),
          title,
          subtitle,
          stackOf(buttonRow(), scanButton),
          pageStatus.label
        )
      );

      // ---- discovered ----
      const discoveredCount = muted('');
      const noDiscovered = emptyState('No devices discovered yet. Run a scan.');
      const discovered = new CardList<DiscoveredDeviceDto>({
        key: peer => peer.id,
        version: rowVersion,
        build: peer => discoveredRow(peer),
        spacing: GAP.md
      });
      const discoveredCard = card();
      discoveredCard.controls.add(
        stackOf(vstack({ spacing: GAP.sm }), h2('Discovered devices'), discoveredCount, noDiscovered.panel, discovered.panel)
      );
      content.controls.add(discoveredCard);

      // ---- add a Synology by IP ----
      const manualHost = vstack({ spacing: GAP.sm });
      let manualPanel: Card | null = null;
      const manualButton = button('Add a Synology device by IP…', () => toggleManual());
      manualButton.enabled = false;
      const manualCard = card();
      manualCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          h2('Synology not on the list?'),
          muted(
            "For a DiskStation or router on another VLAN, or at a site that hasn't been scanned. An on-site agent still does the polling."
          ),
          stackOf(buttonRow(), manualButton),
          manualHost
        )
      );
      content.controls.add(manualCard);

      function toggleManual(): void {
        if (manualPanel) {
          manualHost.controls.remove(manualPanel);
          manualPanel = null;
        } else {
          manualPanel = synologyEnrollPanel({
            ctx,
            customerId,
            discovered: null,
            pollingDeviceId: () => agentId,
            onEnrolled: message => {
              toggleManual();
              pageStatus.ok(message);
              void refreshMonitors();
            }
          }).card;
          manualHost.controls.add(manualPanel);
        }
        relayout(manualHost);
      }

      // ---- monitored ----
      const noMonitors = emptyState('No devices are being monitored yet. Adopt a discovered SNMP device above to start.');
      const monitors = new CardList<SnmpMonitorDto>({
        key: m => m.id,
        build: m => monitorRow(m),
        spacing: GAP.sm
      });
      const monitorsCard = card();
      monitorsCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Monitored devices'), noMonitors.panel, monitors.panel));
      content.controls.add(monitorsCard);

      // ---- deployment log ----
      const noLog = emptyState('No deployments attempted yet.');
      const log = new CardList<DeployLogDto>({ key: entry => entry.id, build: entry => logRow(entry), spacing: GAP.sm });
      const logCard = card();
      logCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Deployment log'), noLog.panel, log.panel));
      content.controls.add(logCard);

      // =================================================================
      //  Rows
      // =================================================================

      function discoveredRow(peer: DiscoveredDeviceDto): Control {
        const pollerId = (): number | null => (peer.discoveredByDeviceId > 0 ? peer.discoveredByDeviceId : agentId);

        const chips = buttonRow({ spacing: GAP.xs, align: 'center' });
        // An unclassified host is "nothing known", not an SNMP device.
        if (peer.kind !== DiscoveredDeviceKind.Unknown) chips.controls.add(chip(kindLabel(peer.kind), 'info'));
        if (peer.snmpEnabled) chips.controls.add(chip('SNMP', 'brand'));
        chips.controls.add(chip(stateLabel(peer), peer.isManaged ? 'ok' : 'neutral'));
        if (peer.adoptedMonitorId !== null) chips.controls.add(chip('Monitored', 'ok'));

        const lines = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
        lines.controls.add(value(peer.hostname || '(unknown)', { font: FONT.bold }));
        lines.controls.add(chips);
        lines.controls.add(
          muted(
            join([
              peer.ipAddress,
              peer.macAddress || 'no MAC',
              peer.openPorts.length > 0 ? 'ports ' + peer.openPorts.join(', ') : 'no open ports'
            ])
          )
        );
        const summary = snmpSummary(peer);
        if (summary) lines.controls.add(muted(summary));
        if (peer.snmpEnabled && peer.snmpLocation.trim()) lines.controls.add(muted('Location: ' + peer.snmpLocation));
        lines.controls.add(muted('Last seen ' + ago(peer.lastSeenUtc)));

        const panels = vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false });
        // One panel open at a time; the button that opened it closes it again.
        let open: { tag: string; panel: Card } | null = null;
        const show = (tag: string, make: () => Card): void => {
          const same = open?.tag === tag;
          if (open) panels.controls.remove(open.panel);
          open = same ? null : { tag, panel: make() };
          if (open) panels.controls.add(open.panel);
          relayout(panels);
        };

        const wakeStatus = new StatusLine();
        const actions = buttonRow();
        if (normalizeMac(peer.macAddress)) {
          actions.controls.add(
            button('Wake', async () => {
              wakeStatus.busy('Sending a wake packet…');
              relayout(wakeStatus.label);
              try {
                const result = await crm.wakeDiscovered(peer.id);
                if (ctx.cancelled()) return;
                if (result.success) wakeStatus.ok(`${result.detail} Re-scan in a minute or two to see whether it came up.`);
                else wakeStatus.error(result.error || result.detail);
              } catch (error) {
                if (!ctx.cancelled()) wakeStatus.error(describeError(error));
              }
              relayout(wakeStatus.label);
            })
          );
        }
        if (canAdopt(peer)) actions.controls.add(button('Monitor', () => show('adopt', () => adoptPanel(peer, pollerId))));
        if (canEnrollSynology(peer))
          actions.controls.add(
            button('Enroll Synology', () =>
              show('synology', () =>
                synologyEnrollPanel({
                  ctx,
                  customerId,
                  discovered: peer,
                  pollingDeviceId: pollerId,
                  onEnrolled: message => {
                    pageStatus.ok(message);
                    void refreshAll();
                  }
                }).card
              )
            )
          );
        if (!peer.isManaged) actions.controls.add(button('Enroll', () => show('enroll', () => enrollPanel(peer))));

        const row = card({ tint: 'alt', bordered: false });
        row.controls.add(
          stackOf(vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }), lines, actions, wakeStatus.label, panels)
        );
        return row;
      }

      function adoptPanel(peer: DiscoveredDeviceDto, pollerId: () => number | null): Card {
        const nameBox = textBox({ text: peer.snmpSysName || peer.hostname || peer.ipAddress });
        const pollBox = textBox({ text: '60' });
        const form = new SnmpForm();
        form.setCommunity(peer.snmpCommunity);
        const status = new StatusLine();

        const panel = card({ tint: 'ok', bordered: false, padding: insets(GAP.md) });

        function credentials(): SnmpCredentials | null {
          const built = form.build();
          if (built.ok) return built.credentials;
          status.error(built.error);
          relayout(panel);
          return null;
        }

        async function probe(): Promise<void> {
          const creds = credentials();
          if (!creds) return;
          const poller = pollerId();
          if (poller === null) {
            status.error('No online agent on this customer to probe from.');
            relayout(panel);
            return;
          }
          status.busy('Probing…');
          relayout(panel);
          try {
            const result = await crm.testCustomerSnmp(customerId, {
              ipAddress: peer.ipAddress,
              credentials: creds,
              pollingDeviceId: poller
            });
            if (ctx.cancelled()) return;
            status.set(join([result.message, result.sysName || null, result.uptimeLabel ? 'up ' + result.uptimeLabel : null]), result.ok ? 'ok' : 'error');
          } catch (error) {
            if (!ctx.cancelled()) status.error(describeError(error));
          }
          relayout(panel);
        }

        async function adopt(): Promise<void> {
          const creds = credentials();
          if (!creds) return;
          const poll = /^\d+$/.test(pollBox.text.trim()) ? Number(pollBox.text.trim()) : 0;
          if (poll <= 0) {
            status.error('Poll every must be a whole number of seconds.');
            relayout(panel);
            return;
          }
          const name = nameBox.text.trim() || peer.ipAddress;
          status.busy('Adopting…');
          relayout(panel);
          try {
            const response = await crm.adoptDiscovered(peer.id, { displayName: name, pollSeconds: poll, credentials: creds });
            if (ctx.cancelled()) return;
            form.clearPasswords();
            // The row is rebuilt by the refresh (it is now adopted), so the outcome goes on the page.
            pageStatus.ok(
              response.agentOnline
                ? `Adopted ${name} — the first poll lands within the poll interval. See Monitored devices below.`
                : `Adopted ${name}, but the polling agent is offline — monitoring starts when it reconnects.`
            );
            await refreshAll();
          } catch (error) {
            if (!ctx.cancelled()) {
              status.error(describeError(error));
              relayout(panel);
            }
          }
        }

        panel.controls.add(
          stackOf(
            vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
            h2('Monitor this device over SNMP'),
            muted('Polls the device on an interval for live metrics. Credentials are stored encrypted.'),
            fieldRow(field('Name', nameBox), field('Poll every (seconds)', pollBox)),
            form.panel,
            stackOf(buttonRow(), button('Test connection', () => void probe()), button('Start monitoring', () => void adopt(), true)),
            status.label
          )
        );
        return panel;
      }

      function enrollPanel(peer: DiscoveredDeviceDto): Card {
        const panel = card({ tint: 'ok', bordered: false, padding: insets(GAP.md) });
        const body = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
        body.controls.add(h2('Enroll this device'));

        if (canDeploy(peer)) {
          const user = textBox({ placeholder: 'Administrator' });
          const password = passwordBox();
          const deployStatus = new StatusLine();
          const install = button(
            'Install remotely',
            async () => {
              if (!user.text.trim() || !password.text) {
                deployStatus.error('Enter admin username and password.');
                relayout(panel);
                return;
              }
              const secret = password.text;
              password.text = '';
              install.enabled = false;
              deployStatus.busy('Deploying…');
              relayout(panel);
              try {
                await crm.deployAgent(peer.id, user.text.trim(), secret);
                if (ctx.cancelled()) return;
                deployStatus.ok(
                  'Install started on the target — it downloads the agent and enrolls in about a minute. Watch the deployment log below for the result.'
                );
              } catch (error) {
                if (!ctx.cancelled()) deployStatus.error(describeError(error));
              } finally {
                // A refusal is written to the log too, so it is refreshed either way.
                if (!ctx.cancelled()) await refreshLog();
                install.enabled = true;
                relayout(panel);
              }
            },
            true
          );
          body.controls.add(
            stackOf(
              vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
              text('Install automatically', { font: FONT.bold }),
              muted(
                'Pushes the agent from your on-site agent over remote management (WMI, then SMB, then WinRM). Admin credentials are used once and never stored.'
              ),
              fieldRow(field('Admin username', user), field('Password', password)),
              stackOf(buttonRow(), install),
              deployStatus.label
            )
          );
        }

        const synology = looksSynology(peer);
        const tokenStatus = new StatusLine();
        const output = vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false, visible: false });
        const generate = button('Generate enrollment token', async () => {
          generate.enabled = false;
          tokenStatus.busy('Minting a token…');
          relayout(panel);
          try {
            const info = await crm.mintDiscoveredEnrollToken(peer.id);
            if (ctx.cancelled()) return;
            tokenStatus.set('');
            output.controls.clear();
            output.controls.add(field('Token', textBox({ text: info.token, readOnly: true })));
            if (synology) {
              output.controls.add(field('Server URL (paste into the package’s install wizard)', textBox({ text: info.serverUrl, readOnly: true })));
              output.controls.add(
                muted(
                  'In DSM or SRM: Package Center → Manual Install → the JetsonCRM agent .spk, then enter the server URL and the token above. The device appears under devices awaiting approval a minute or so later.'
                )
              );
            } else {
              output.controls.add(field('Run on the device in an elevated PowerShell', textBox({ text: info.installCommand, readOnly: true })));
              output.controls.add(
                muted(
                  'Valid 72 hours, single use. Downloads the agent from this server (the token authorizes the download), installs it as a service, and enrolls — no manual copy needed. Run as Administrator.'
                )
              );
            }
            output.visible = true;
          } catch (error) {
            if (!ctx.cancelled()) tokenStatus.error(describeError(error));
          } finally {
            generate.enabled = true;
            relayout(panel);
          }
        });
        body.controls.add(
          stackOf(
            vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
            text('Install manually', { font: FONT.bold }),
            muted(
              synology
                ? 'Install the JetsonCRM agent package on the device. Generate a token, then paste the server URL and token into the package’s install wizard.'
                : 'For devices without remote management, or off-network. Generate a one-time token, then run the agent on the device with it.'
            ),
            stackOf(buttonRow(), generate),
            tokenStatus.label,
            output
          )
        );

        panel.controls.add(body);
        return panel;
      }

      function monitorRow(m: SnmpMonitorDto): Control {
        const chips = buttonRow({ spacing: GAP.xs, align: 'center' });
        chips.controls.add(
          m.lastPolledUtc === null ? chip('Never polled', 'neutral') : m.online ? chip('Online', 'ok') : chip('Offline', 'danger')
        );
        chips.controls.add(chip(kindLabel(m.kind), 'info'));
        if (m.hasApiCredentials) chips.controls.add(chip('Web API', 'brand'));
        if (m.agentDeviceId !== null) chips.controls.add(chip('Agent installed', 'ok'));

        const lines = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
        lines.controls.add(value(m.displayName, { font: FONT.bold }));
        lines.controls.add(chips);
        lines.controls.add(
          muted(
            join([
              m.ipAddress,
              m.snmpVersionLabel,
              `every ${m.pollSeconds} s`,
              m.lastPolledUtc ? 'last polled ' + ago(m.lastPolledUtc) : null
            ])
          )
        );
        if (m.agentDeviceId !== null)
          lines.controls.add(muted(`Enrolled in the fleet as ${m.agentDeviceName || 'a device'} — SNMP shows on that device's page.`));

        const rowStatus = new StatusLine();
        const actions = buttonRow();
        actions.controls.add(button('Open', () => ctx.go(`/monitors/${m.id}`), true));
        if (m.agentDeviceId !== null) actions.controls.add(button('Open device →', () => ctx.go(`/devices/${m.agentDeviceId}`)));
        actions.controls.add(
          button('Remove', async () => {
            const yes = await confirm(ctx.desktop, {
              title: 'Stop monitoring',
              message: `Stop monitoring ${m.displayName}? Its stored readings, graphs and credentials are deleted. The discovered device stays, and can be adopted again.`
            });
            if (!yes || ctx.cancelled()) return;
            rowStatus.busy('Removing…');
            relayout(rowStatus.label);
            try {
              await crm.removeMonitor(m.id);
              if (ctx.cancelled()) return;
              pageStatus.ok(`Stopped monitoring ${m.displayName}.`);
              await refreshAll();
            } catch (error) {
              if (!ctx.cancelled()) {
                rowStatus.error(describeError(error));
                relayout(rowStatus.label);
              }
            }
          })
        );

        const row = card({ tint: 'alt', bordered: false });
        row.controls.add(stackOf(vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }), lines, actions, rowStatus.label));
        return row;
      }

      function logRow(entry: DeployLogDto): Control {
        const top = splitRow({ spacing: GAP.sm, align: 'start' });
        fillWith(top, value(join([entry.targetIp, entry.targetHostname || null]), { font: FONT.bold }));
        top.controls.add(chip(entry.stage || (entry.success ? 'ok' : 'failed'), entry.success ? 'ok' : 'danger'));
        const lines = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
        lines.controls.add(top);
        if (entry.message) lines.controls.add(text(entry.message));
        lines.controls.add(muted(join([localDateTime(entry.whenUtc), entry.initiatedByUser || null])));
        const row = card({ tint: 'alt', bordered: false });
        row.controls.add(lines);
        return row;
      }

      // =================================================================
      //  Loading
      // =================================================================

      async function refreshDiscovered(): Promise<void> {
        try {
          const peers = await crm.getDiscovered(customerId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          discovered.set(peers);
          discoveredCount.text =
            peers.length === 0 ? '' : `${plural(peers.length, 'device')} found by this customer's agents.`;
          discoveredCount.visible = peers.length > 0;
          noDiscovered.update(peers.length === 0 && agentId !== null ? 0 : 1);
          if (scanning) {
            scanning = false;
            scanButton.enabled = agentId !== null;
            pageStatus.ok(`Found ${plural(peers.length, 'device')}.`);
          }
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load the discovered devices: ' + describeError(error));
        }
        relayout(discovered.panel);
      }

      async function refreshMonitors(): Promise<void> {
        try {
          const list = await crm.getCustomerMonitors(customerId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          monitors.set(list);
          noMonitors.update(list.length);
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load the monitored devices: ' + describeError(error));
        }
        relayout(monitors.panel);
      }

      async function refreshLog(): Promise<void> {
        try {
          const entries = await crm.getDeployLog(customerId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          log.set(entries);
          noLog.update(entries.length);
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load the deployment log: ' + describeError(error));
        }
        relayout(log.panel);
      }

      async function refreshAll(): Promise<void> {
        await Promise.all([refreshDiscovered(), refreshMonitors(), refreshLog()]);
      }

      async function scan(): Promise<void> {
        if (agentId === null || scanning) return;
        scanning = true;
        scanButton.enabled = false;
        pageStatus.busy('Scanning the network…');
        try {
          await crm.scanNetwork(agentId);
        } catch (error) {
          if (ctx.cancelled()) return;
          scanning = false;
          scanButton.enabled = true;
          pageStatus.error('Could not start the scan: ' + describeError(error));
        }
      }

      void (async () => {
        try {
          const [customer, devices] = await Promise.all([
            crm.getCustomer(customerId, ctx.dispose.signal()),
            crm.getDevices(customerId, ctx.dispose.signal())
          ]);
          if (ctx.cancelled()) return;
          title.text = `Network — ${customer.name}`;
          const online = devices.find(d => d.presence?.isOnline);
          agentId = online?.id ?? null;
          subtitle.text = online
            ? `Scans and polls run from ${online.name}, an online agent on this customer.`
            : 'No online agent on this customer to scan from. Discovery, monitoring and installs need one.';
          scanButton.enabled = manualButton.enabled = agentId !== null;
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load this customer: ' + describeError(error));
        }
        await refreshAll();
        if (ctx.cancelled()) return;
        if (await hub.ensureConnected()) {
          if (ctx.cancelled()) return;
          ctx.dispose.add(
            hub.on('DiscoveryChanged', changed => {
              if (changed === customerId) void refreshAll();
            })
          );
          ctx.dispose.add(
            hub.on('MonitorChanged', changed => {
              if (changed === customerId) void refreshMonitors();
            })
          );
        }
      })();
    }
  };
}
